import { SessionDeadline } from '@dealstack/frontend/shared/mechanism/session/sessionDeadline';

export const SESSION_TICK_MS = 1000;

// A third of the idle window, so an interacting user has two spare chances before it
// would have closed, at a cost of at most three small requests per window.
export const KEEP_ALIVE_FRACTION_OF_IDLE_WINDOW = 1 / 3;

/**
 * How stale the last interaction may be for a keep-alive to still be honest. Bounds the
 * whole control: a keep-alive moves the server's deadline a full idle window from *now*,
 * so a stale interaction would turn a 30-minute policy into 30 plus that staleness.
 * Sized to tolerate a throttled ticker, not to tolerate absence.
 */
export const MAX_INTERACTION_AGE_FOR_KEEP_ALIVE_MS = 15 * 1000;

export const msUntilExpiry = (deadline: SessionDeadline, now: number): number =>
	Math.min(deadline.idleExpiresAt, deadline.absoluteExpiresAt) - now;

export const secondsUntilExpiry = (deadline: SessionDeadline, now: number): number =>
	Math.max(0, Math.ceil(msUntilExpiry(deadline, now) / 1000));

export const isWithinWarningWindow = (
	deadline: SessionDeadline,
	now: number,
	warningSeconds: number
): boolean => {
	const remaining = msUntilExpiry(deadline, now);

	return remaining > 0 && remaining <= warningSeconds * 1000;
};

export const hasExpired = (deadline: SessionDeadline, now: number): boolean =>
	msUntilExpiry(deadline, now) <= 0;

/**
 * Also true inside the warning window, so someone who starts working again as the dialog
 * appears is carried through without clicking anything.
 */
export const shouldSendKeepAlive = ({
	deadline,
	now,
	lastKeepAliveAt,
	idleTimeoutSeconds,
	warningSeconds,
	msSinceLastInteraction,
}: {
	deadline: SessionDeadline;
	now: number;
	lastKeepAliveAt: number;
	idleTimeoutSeconds: number;
	warningSeconds: number;
	msSinceLastInteraction: number;
}): boolean => {
	const remaining = msUntilExpiry(deadline, now);
	if (remaining <= 0) {
		return false;
	}

	// The absolute cap cannot be extended.
	if (deadline.absoluteExpiresAt <= now) {
		return false;
	}

	// "Here now", not "here during this window". Everything below only decides how often
	// a present user costs a request.
	if (msSinceLastInteraction > MAX_INTERACTION_AGE_FOR_KEEP_ALIVE_MS) {
		return false;
	}

	const keepAliveInterval = idleTimeoutSeconds * 1000 * KEEP_ALIVE_FRACTION_OF_IDLE_WINDOW;
	const dueByInterval = now - lastKeepAliveAt >= keepAliveInterval;
	const closeToExpiry = remaining <= (warningSeconds + 60) * 1000;

	return dueByInterval || closeToExpiry;
};
